import { skipToken } from '@reduxjs/toolkit/query';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { CREATE_DIALOG_ACTION_CLASS } from 'components/buttons/CancelButton';
import { useDraftSubmit } from 'components/drafts/DraftSaveMode';
import { Button } from 'components/ui/button';
import upperFirst from 'lodash/upperFirst';
import { ReactNode, RefObject, useCallback, useEffect, useRef } from 'react';
import type { FieldValues, FormState, UseFormReturn } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'store';
import {
    useCompleteDraftMutation,
    useCreateDraftMutation,
    useGetDraftQuery,
    useSaveDraftMutation,
} from 'store/api/drafts';
import { DraftDetail, DraftKey } from 'types/drafts';
import { FeatureFlag } from 'types/features';
import { successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useLeaveForm } from 'utils/useNavigateWithReturnState';
import { useCreateDialogRequest, useCreateRoute } from './CreateDialogContext';
import { CreateRecordType, draftPageOf, isDraftType } from './recordTypes';

/** The draft a create URL edits: `?draft=<id>`, the id of the draft's record. */
export function draftIdIn(search: string | URLSearchParams): number | null {
    const id = Number(new URLSearchParams(search).get('draft'));
    return Number.isInteger(id) && id > 0 ? id : null;
}

/** The draft the create dialog's creation edits, or `null` where it creates a record afresh. */
export function useDialogDraftKey(): DraftKey | null {
    const request = useCreateDialogRequest();
    const id = request ? draftIdIn(request.location.search) : null;
    return request && id !== null && isDraftType(request.record) ? { type: request.record, id } : null;
}

/**
 * Hands `children` the draft the dialog edits, once loaded. A component rather than a `skipToken` query in the frame,
 * so that a dialog that edits no draft calls no store hook: `renderInCreateDialog` renders the frame with no store.
 */
export function DialogDraft({
    draft,
    children,
}: {
    draft: DraftKey;
    children: (detail: DraftDetail | undefined) => ReactNode;
}) {
    return children(useGetDraftQuery(draft).data);
}

/** What leaving a create page with unsaved changes offers besides leaving: saving them as a draft. */
export type DraftLeave = {
    record: CreateRecordType;
    save: () => Promise<boolean>;
    /** Set once Save as draft has saved the form and leaves it for the draft's page: nothing is left to ask. */
    leaving: RefObject<boolean>;
};

type Options<T extends FieldValues> = {
    record: CreateRecordType;
    /**
     * A form without react-hook-form hands a `handleSubmit` of its own, which passes its values on as they are.
     * `formState.isDirty` is whether the user filled a field, against the form's own defaults: a value the form fills
     * in is not one.
     */
    methods: Pick<UseFormReturn<T>, 'handleSubmit'> & { formState: Pick<FormState<T>, 'isDirty'> };
    /** The body the record type's create takes, or `null` where the form refuses the values itself and shows why. */
    toPayload: (values: T) => unknown;
    /** A creation no draft can hold: one on an invoice, which a draft of its record type would not be on. */
    withoutDraft?: boolean;
};

function SaveAsDraftButton({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
    const inCreateDialog = useCreateDialogRequest() !== null;
    const buttonsDisabled = useSelector((state) => state.buttons.buttonsDisabled);

    return (
        <Button
            {...buttonStyle({ variant: 'outlined' }, inCreateDialog ? CREATE_DIALOG_ACTION_CLASS : undefined)}
            onClick={onClick}
            disabled={buttonsDisabled || disabled}
        >
            <FormattedMessage id="create-dialog.actions.save-draft" />
        </Button>
    );
}

/**
 * Save as draft for a create form, in the create dialog and on its page alike. The form renders its fields inside
 * `<DraftSaveMode mode={mode}>`, lists `button` right after its Cancel, hands `leave` to its `ConfirmNavigation`,
 * passes `detail` to its `ActionHeader`, and creates through `complete` while `draftId` is set: the create URL with
 * `?draft=<id>` edits that draft, whose main action completes it.
 *
 * A draft save runs the form's rules but the required ones and stores what it holds, with the labels of the required
 * fields left empty. Saved, the form leaves for the draft's page as a create leaves for the new record's: the dialog's
 * creation ends and the app goes there, unless the dialog stays for the next creation, or is over that page already
 * because it edits the draft. The leave question's save goes on where the user was going.
 * A new draft waits for a field the user filled: until then the button is there but disabled.
 * Where the record type has no drafts, the creation cannot be one (`withoutDraft`), or the flag is off, there is no
 * button and nothing to leave by: an existing draft is still completed.
 */
export function useCreateDraft<T extends FieldValues>({
    record,
    methods,
    toPayload,
    withoutDraft = false,
}: Options<T>) {
    const isEnabled = useIsFeatureEnabled(FeatureFlag.SAVE_AS_DRAFT);
    const { location, inDialog } = useCreateRoute();
    const request = useCreateDialogRequest();
    const navigate = useNavigate();
    const [createDraft] = useCreateDraftMutation();
    const [saveDraft] = useSaveDraftMutation();
    const [completeDraft] = useCompleteDraftMutation();
    const type = isDraftType(record) ? record : null;
    const draftId = draftIdIn(location.search);
    // `?fill` asks for the first required field the draft misses: its record page's "Fill them in".
    const fill = draftId !== null && new URLSearchParams(location.search).has('fill');
    const savedRef = useRef<DraftDetail | null>(null);
    const leavingRef = useRef(false);
    const leaveForm = useLeaveForm();
    const { data: detail } = useGetDraftQuery(type && draftId !== null ? { type, id: draftId } : skipToken);

    const { mode, saveAsDraft } = useDraftSubmit(
        methods,
        async (values, missingFields) => {
            const payload = await toPayload(values);
            if (payload === null || !type) {
                return;
            }
            const body = { type, payload, missingFields };
            try {
                const saved = await (draftId ? saveDraft({ ...body, id: draftId }) : createDraft(body)).unwrap();
                savedRef.current = saved;
                successAlert(t('draft.toast.saved', { record: upperFirst(t(`create-dialog.record.${record}`)) }));
            } catch {
                // Refused: the error handler's toast says why, and the form stays as it is.
            }
        },
        fill
    );

    /** The draft as saved, or `null` when a rule or the server refused it. */
    const save = useCallback(async () => {
        savedRef.current = null;
        await saveAsDraft();
        // Set by the save's own callback, which the narrowing above cannot see.
        return savedRef.current as DraftDetail | null;
    }, [saveAsDraft]);

    const offered = isEnabled && type !== null && !withoutDraft;
    // Read first, so the form tracks it whether or not a draft is being edited, which saves again as it is.
    const filled = methods.formState.isDirty || draftId !== null;
    const finish = request?.finish;

    // The dialog holds the save it offers as state that every form under it reads: a new one on each render would
    // register again and render the form again, without end. It keeps one, which calls the form's latest save.
    const saveRef = useRef(save);
    useEffect(() => {
        saveRef.current = save;
    });
    const saveAndLeave = useCallback(async () => {
        const saved = await saveRef.current();
        if (saved && finish) {
            finish(undefined, { draft: true });
        }
        return saved !== null;
    }, [finish]);

    const registerDraftSave = request?.registerDraftSave;
    useEffect(() => {
        registerDraftSave?.(offered ? saveAndLeave : null);
        return () => registerDraftSave?.(null);
    }, [registerDraftSave, offered, saveAndLeave]);

    const saveFromButton = async () => {
        const saved = await save();
        if (!saved || !type) {
            return;
        }
        const page = draftPageOf(type, saved.id);
        leavingRef.current = true;
        leaveForm(page, { draft: true });
        // The dialog's end goes nowhere by itself. A draft it edits was opened from its page, which is still behind it.
        if (inDialog && saved.id !== draftId && !request?.createAnother) {
            navigate(page);
        }
    };

    return {
        mode,
        draftId,
        /** The draft being edited, once loaded. */
        detail,
        button: offered ? <SaveAsDraftButton key="saveAsDraft" onClick={saveFromButton} disabled={!filled} /> : null,
        leave: offered
            ? ({ record, save: async () => (await save()) !== null, leaving: leavingRef } satisfies DraftLeave)
            : null,
        /** Creates the record the draft being edited holds, with every rule of its type. */
        complete: (payload: unknown) => {
            if (!type || draftId === null) {
                throw new Error('No draft to complete');
            }
            return completeDraft({ type, id: draftId, payload }).unwrap();
        },
    };
}
